import { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../../components/Layout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, FileText, Download, Trash2, Building2, Calendar } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = [
  { value: 1, label: 'Enero' },
  { value: 2, label: 'Febrero' },
  { value: 3, label: 'Marzo' },
  { value: 4, label: 'Abril' },
  { value: 5, label: 'Mayo' },
  { value: 6, label: 'Junio' },
  { value: 7, label: 'Julio' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Septiembre' },
  { value: 10, label: 'Octubre' },
  { value: 11, label: 'Noviembre' },
  { value: 12, label: 'Diciembre' }
];

export default function AdminInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    company_id: '',
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    description: '',
    file: null
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [invoicesRes, companiesRes] = await Promise.all([
        axios.get(`${API}/invoices`),
        axios.get(`${API}/companies`)
      ]);
      setInvoices(invoicesRes.data);
      setCompanies(companiesRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setFormData({ ...formData, file });
    } else {
      toast.error('Por favor, selecciona un archivo PDF');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.company_id || !formData.file) {
      toast.error('Empresa y archivo PDF son requeridos');
      return;
    }

    setUploading(true);
    try {
      // Convert file to base64
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result.split(',')[1];
        
        await axios.post(`${API}/invoices`, {
          company_id: formData.company_id,
          month: formData.month,
          year: formData.year,
          description: formData.description,
          file_data: base64,
          file_name: formData.file.name
        });
        
        toast.success('Factura subida correctamente');
        setDialogOpen(false);
        setFormData({
          company_id: '',
          month: new Date().getMonth() + 1,
          year: new Date().getFullYear(),
          description: '',
          file: null
        });
        fetchData();
        setUploading(false);
      };
      reader.readAsDataURL(formData.file);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir factura');
      setUploading(false);
    }
  };

  const handleDownload = async (invoice) => {
    try {
      const response = await axios.get(`${API}/invoices/${invoice.id}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', invoice.file_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Error al descargar factura');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar esta factura?')) return;
    
    try {
      await axios.delete(`${API}/invoices/${id}`);
      toast.success('Factura eliminada');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar factura');
    }
  };

  const getMonthName = (month) => MONTHS.find(m => m.value === month)?.label || month;

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  return (
    <Layout title="Gestión de Facturas">
      <div className="space-y-6 animate-fade-in" data-testid="admin-invoices">
        {/* Header */}
        <div className="flex items-center justify-between">
          <p className="text-slate-400">Sube y gestiona las facturas mensuales de cada empresa</p>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-sky-600 hover:bg-sky-500 text-white gap-2" data-testid="add-invoice-btn">
                <Plus className="w-4 h-4" />
                Subir Factura
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-rajdhani text-xl">Subir Factura</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Empresa *</Label>
                  <Select
                    value={formData.company_id}
                    onValueChange={(v) => setFormData({ ...formData, company_id: v })}
                  >
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="invoice-company-select">
                      <SelectValue placeholder="Seleccionar empresa" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {companies.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Mes *</Label>
                    <Select
                      value={String(formData.month)}
                      onValueChange={(v) => setFormData({ ...formData, month: parseInt(v) })}
                    >
                      <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="invoice-month-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        {MONTHS.map((m) => (
                          <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Año *</Label>
                    <Select
                      value={String(formData.year)}
                      onValueChange={(v) => setFormData({ ...formData, year: parseInt(v) })}
                    >
                      <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="invoice-year-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        {years.map((y) => (
                          <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Descripción (opcional)</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white"
                    placeholder="Factura mensual de servicios..."
                    data-testid="invoice-description-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Archivo PDF *</Label>
                  <Input
                    type="file"
                    accept=".pdf"
                    onChange={handleFileChange}
                    className="bg-slate-800 border-slate-700 text-white file:bg-slate-700 file:text-white file:border-0 file:mr-4"
                    data-testid="invoice-file-input"
                  />
                  {formData.file && (
                    <p className="text-sm text-slate-400">Archivo seleccionado: {formData.file.name}</p>
                  )}
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)} className="text-slate-400">
                    Cancelar
                  </Button>
                  <Button type="submit" className="bg-sky-600 hover:bg-sky-500" disabled={uploading} data-testid="save-invoice-btn">
                    {uploading ? 'Subiendo...' : 'Subir Factura'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Invoices List */}
        {loading ? (
          <div className="text-center py-12 text-slate-500">Cargando...</div>
        ) : invoices.length === 0 ? (
          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="py-12 text-center">
              <FileText className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No hay facturas registradas</p>
              <p className="text-sm text-slate-500 mt-1">Sube la primera factura para comenzar</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {invoices.map((invoice) => (
              <Card key={invoice.id} className="bg-slate-900/50 border-slate-800 hover:border-slate-700 transition-all">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg bg-amber-500/10 flex items-center justify-center">
                        <FileText className="w-6 h-6 text-amber-400" />
                      </div>
                      <div>
                        <h3 className="font-medium text-white">{invoice.file_name}</h3>
                        <div className="flex items-center gap-4 mt-1">
                          <span className="flex items-center gap-1 text-sm text-slate-400">
                            <Building2 className="w-4 h-4" />
                            {invoice.company_name}
                          </span>
                          <span className="flex items-center gap-1 text-sm text-slate-400">
                            <Calendar className="w-4 h-4" />
                            {getMonthName(invoice.month)} {invoice.year}
                          </span>
                        </div>
                        {invoice.description && (
                          <p className="text-sm text-slate-500 mt-1">{invoice.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDownload(invoice)}
                        className="text-slate-400 hover:text-sky-400 hover:bg-sky-500/10"
                        data-testid={`download-invoice-${invoice.id}`}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(invoice.id)}
                        className="text-slate-400 hover:text-rose-400 hover:bg-rose-500/10"
                        data-testid={`delete-invoice-${invoice.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
